// Centralized analytics API. Components call ONLY these four functions and
// never touch gtag/posthog-js/aws-rum-web directly, so providers can be
// swapped/added/removed independently (see providers/ga4.js,
// providers/posthog.js, providers/rum.ts).
//
// Consent: GA4 is already gated by iubenda's Google Consent Mode v2
// integration (public/index.html) and is safe to always call - Consent Mode
// itself decides what actually gets sent. PostHog and RUM have no such
// built-in awareness, so both are explicitly opted in/out here as consent
// changes (see providers/posthog.ts's and providers/rum.ts's setConsent()).
//
// Dev safeguard: outside a production build, events are logged to the
// console instead of being sent, unless REACT_APP_ANALYTICS_FORCE_ENABLE is
// set (for deliberately testing against a real GA4/PostHog project locally).
// initRum() follows the same rule.
import * as ga4 from "./providers/ga4";
import * as posthogProvider from "./providers/posthog";
import * as rumProvider from "./providers/rum";
import { sanitizeProperties } from "./sanitize";
import { hasAnalyticsConsent, onConsentChange } from "./consent";
import type { AnalyticsProperties } from "./types";

const isProduction = process.env.NODE_ENV === "production";
const debugEnabled = !isProduction || process.env.REACT_APP_ANALYTICS_DEBUG === "true";
const providersEnabled = isProduction || process.env.REACT_APP_ANALYTICS_FORCE_ENABLE === "true";

function debugLog(label: string, ...args: unknown[]): void {
    if (debugEnabled) console.info(`[analytics] ${label}`, ...args);
}

onConsentChange((granted) => {
    posthogProvider.setConsent(granted);
    rumProvider.setConsent(granted);
});
// Apply current consent state immediately too, in case it was already known
// (e.g. onConsentGiven fired for a returning visitor) before this module's
// listener was registered.
posthogProvider.setConsent(hasAnalyticsConsent());
// RUM's client doesn't exist yet at this point (it's only constructed by
// initRum(), called later from src/index.tsx) - rumProvider.setConsent() is
// applied from inside initRum() itself instead, right after init(), so it
// still picks up whatever consent state is already known by then.

export function page(path: string): void {
    debugLog("page", path);
    if (!providersEnabled) return;
    ga4.pageview(path);
    posthogProvider.pageview(path);
}

export function track(name: string, properties: AnalyticsProperties = {}): void {
    const safeProperties = sanitizeProperties(properties);
    debugLog("track", name, safeProperties);
    if (!providersEnabled) return;
    ga4.event(name, safeProperties);
    posthogProvider.event(name, safeProperties);
    rumProvider.recordEvent(name, safeProperties);
}

export function identify(userId: string | null | undefined): void {
    if (!userId) return;
    debugLog("identify", userId);
    if (!providersEnabled) return;
    ga4.identify(userId);
    posthogProvider.identify(userId);
}

export function reset(): void {
    debugLog("reset");
    if (!providersEnabled) return;
    ga4.reset();
    posthogProvider.reset();
}

// Called once, as early as possible, from src/index.tsx - deliberately not
// a side effect of importing this module, since RUM needs to start
// capturing real page-load performance timing before the service-worker
// "settle" check in src/index.tsx runs, not whenever App.tsx happens to
// import ../analytics. See providers/rum.ts for why page views/errors/
// performance/http are otherwise collected automatically rather than
// through page()/track() above.
export function initRum(): void {
    debugLog("initRum");
    if (!providersEnabled) return;
    rumProvider.init();
    // Apply whatever consent state is already known now that the client
    // exists - see the comment above onConsentChange() for why this can't
    // just happen at module-load time the way PostHog's does.
    rumProvider.setConsent(hasAnalyticsConsent());
}
